import React, { useCallback, useEffect, useState } from 'react';
import { Box, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { getReadableTextColor, hasReadableContrast, shiftToneForSubtleContrast } from '@shared/utils/color';
import { useSmartAppBannerProvider } from '@shared/components/SmartAppBanner';
import { useWebsiteBannerOffset } from '@shared/components/WebsiteBanner';
import { useColorPaletteProvider } from '../../layouts/ColorPaletteProvider/ColorPaletteProvider';

export type WeddingCodeBannerProps = Readonly<{
  /** The full banner copy, with the wedding code already interpolated. */
  message: string;
  /** Concise accessible name announced by screen readers instead of the full marketing copy. */
  ariaLabel: string;
  /** Invoked when the guest activates the banner — navigates to the accommodations page. */
  onNavigate: () => void;
}>;

/**
 * Guest-facing wedding-code banner — a full-width strip pinned at the very top of the wedding
 * website (above the hero), colored from the couple's own theme and linking to accommodations.
 *
 * It is `position: fixed` and sits below any smart-app / website banner (`top` = their offset).
 * Because it is fixed it occupies no layout space; `WeddingCodeBannerSlot` holds that space in
 * normal flow instead, mounted alongside this component, so the banner covers its own slot rather
 * than the page content (PRNT-4836).
 *
 * Its height is intrinsic: the copy wraps freely and the bar grows with it, which matters on narrow
 * viewports where a long code or a translated string needs two or three lines. The slot lays out
 * the same copy invisibly, so it grows by exactly the same amount — the two stay aligned without
 * either side being clamped or measured.
 *
 * Colors come from `useColorPaletteProvider()`: the bar is derived from `artBackgroundColor` and the
 * text uses the couple's `artBackgroundFontColor` (matching the header, per the Figma). Rather than
 * using the theme color verbatim — which makes the bar blend into the page — we nudge it a few tones
 * (lighter for dark palettes, darker for light ones) so the bar stays cohesive but stands out subtly.
 * If the theme font color wouldn't be legible on the shifted bar — or is missing — it falls back to a
 * derived readable color.
 */
export const WeddingCodeBanner: React.FC<WeddingCodeBannerProps> = ({ message, ariaLabel, onNavigate }) => {
  const { artBackgroundColor, artBackgroundFontColor } = useColorPaletteProvider();
  const { smartAppBannerHeight } = useSmartAppBannerProvider();
  const { bannerOffset } = useWebsiteBannerOffset();

  // Shift a few tones off the theme background so the bar separates subtly from the page instead of
  // blending in (per design). Legibility is re-checked against this shifted color, not the original.
  // The hardcoded fallback is used verbatim — with no theme color there's nothing to offset from.
  const backgroundColor = artBackgroundColor ? shiftToneForSubtleContrast(artBackgroundColor) : '#1d1d1f';
  const themeFontColorIsLegible = Boolean(artBackgroundFontColor) && hasReadableContrast(backgroundColor, artBackgroundFontColor as string);
  const textColor = themeFontColorIsLegible ? (artBackgroundFontColor as string) : getReadableTextColor(backgroundColor);

  // Sit below any smart-app / website banner already pinned at the top.
  const topOffset = Math.max(smartAppBannerHeight, bannerOffset);

  // Blend in rather than appearing fully formed. The slot reserves the space in the same commit, so
  // the bar fades its color and copy in over whatever sits behind it — a solid theme, a cover photo
  // or a video — instead of a hard-edged strip snapping into place.
  const [hasSettled, setHasSettled] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setHasSettled(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onNavigate();
      }
    },
    [onNavigate],
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onNavigate}
      onKeyDown={handleKeyDown}
      position="fixed"
      top={topOffset}
      left={0}
      right={0}
      width="100%"
      zIndex="banner"
      display="flex"
      alignItems="center"
      justifyContent="center"
      paddingX={6}
      paddingY={pxToRem(12)}
      style={{
        backgroundColor,
        opacity: hasSettled ? 1 : 0,
        transition: 'opacity 400ms ease-out',
        cursor: 'pointer',
      }}
    >
      <TextV2
        typographyVariant={{ _: 'label2', md: 'label3' }}
        textAlign="center"
        style={{ color: textColor }}
      >
        {message}
      </TextV2>
    </Box>
  );
};

WeddingCodeBanner.displayName = 'WeddingCodeBanner';
